import React, { useState } from 'react';
import { Zap, ArrowRight, Play, Clock, GitBranch, Webhook, FileText, Bot, Database } from 'lucide-react';

const STARTER_TEMPLATES = [
  {
    id: 'form-to-crm',
    label: 'Form to CRM',
    description: 'Save form responses as contacts',
    trigger: 'formSubmission',
    actions: ['createRecord'],
    icon: FileText,
    accentColor: '#64748b',
  },
  {
    id: 'lead-qualify',
    label: 'AI Lead Qualification',
    description: 'Score and enrich new leads with AI',
    trigger: 'formSubmission',
    actions: ['callLLM', 'createRecord'],
    icon: Bot,
    accentColor: '#8b5cf6',
  },
  {
    id: 'webhook-to-ai',
    label: 'Webhook to AI',
    description: 'Process incoming data with AI',
    trigger: 'webhook',
    actions: ['callLLM'],
    icon: Webhook,
    accentColor: '#64748b',
  },
  {
    id: 'client-onboarding',
    label: 'Client Onboarding',
    description: 'New form signup triggers welcome email',
    trigger: 'formSubmission',
    actions: ['createRecord', 'sendEmail'],
    icon: FileText,
    accentColor: '#64748b',
  },
];

const TRIGGER_OPTIONS = [
  {
    type: 'formSubmission',
    label: 'When Form is Submitted',
    description: 'Runs when someone submits one of your forms',
    icon: FileText,
  },
  {
    type: 'webhook',
    label: 'Incoming Webhook',
    description: 'Runs when an external app sends data to a URL',
    icon: Webhook,
  },
  {
    type: 'cron',
    label: 'On a Schedule',
    description: 'Runs automatically at set times you choose',
    icon: Clock,
  },
  {
    type: 'workflowTrigger',
    label: 'When Workflow Finishes',
    description: 'Runs after another workflow completes',
    icon: GitBranch,
  },
  {
    type: 'recordChange',
    label: 'When Record Changes',
    description: 'Runs when a CRM record is created, updated, or deleted',
    icon: Database,
  },
  {
    type: 'manual',
    label: 'Run Manually',
    description: 'Only runs when you click the Run button',
    icon: Play,
  },
];

export default function EmptyCanvasOverlay({ onAddTemplate, onAddTrigger }) {
  const [view, setView] = useState('templates'); // 'templates' | 'triggers'

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
      <div className="pointer-events-auto max-w-lg w-full mx-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

          {view === 'templates' ? (
            <>
              {/* Header */}
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-[15px] font-semibold text-gray-900 mb-1">
                  Build your first workflow
                </h2>
                <p className="text-[13px] text-gray-500 leading-relaxed">
                  Workflows automate tasks by connecting a <strong className="text-gray-700">trigger</strong> (what starts it)
                  to one or more <strong className="text-gray-700">actions</strong> (what it does).
                  Pick a starting point below, or add a trigger from the sidebar.
                </p>
              </div>

              {/* How it works — minimal diagram */}
              <div className="mx-6 mb-4 flex items-center gap-3 px-4 py-3 bg-gray-50/80 rounded-xl">
                <div className="flex items-center gap-2 text-[12px] text-gray-500">
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-lg border border-gray-200">
                    <Zap className="w-3 h-3 text-gray-400" />
                    <span className="font-medium text-gray-600">Trigger</span>
                  </div>
                  <ArrowRight className="w-3 h-3 text-gray-300" />
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-lg border border-gray-200">
                    <Play className="w-3 h-3 text-gray-400" />
                    <span className="font-medium text-gray-600">Action</span>
                  </div>
                  <ArrowRight className="w-3 h-3 text-gray-300" />
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-lg border border-gray-200">
                    <Play className="w-3 h-3 text-gray-400" />
                    <span className="font-medium text-gray-600">Action</span>
                  </div>
                </div>
              </div>

              {/* Starter templates */}
              <div className="px-6 pb-2">
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Quick start
                </div>
              </div>

              <div className="px-6 pb-5 grid grid-cols-2 gap-2">
                {STARTER_TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => onAddTemplate(template)}
                      className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-300 hover:bg-gray-50/50 transition-all text-left group cursor-pointer"
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${template.accentColor}08` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: template.accentColor }} strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-gray-800 group-hover:text-gray-900">
                          {template.label}
                        </div>
                        <div className="text-[11px] text-gray-400 leading-snug mt-0.5">
                          {template.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Footer — switch to triggers */}
              <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50">
                <button
                  onClick={() => setView('triggers')}
                  className="text-[12px] text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Or start from scratch with a trigger &rarr;
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Header — trigger picker */}
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-[15px] font-semibold text-gray-900 mb-1">
                  Choose a trigger
                </h2>
                <p className="text-[13px] text-gray-500 leading-relaxed">
                  Every workflow starts with a trigger — the event that kicks things off.
                  Pick the one that fits how you want this workflow to start.
                </p>
              </div>

              {/* Trigger list */}
              <div className="mx-6 mb-4 flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                {TRIGGER_OPTIONS.map((trigger) => {
                  const Icon = trigger.icon;
                  return (
                    <button
                      key={trigger.type}
                      onClick={() => onAddTrigger(trigger.type)}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-all text-left group cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                        <Icon className="w-3.5 h-3.5 text-gray-500" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium text-gray-800 group-hover:text-gray-900">
                          {trigger.label}
                        </div>
                        <div className="text-[11px] text-gray-400 leading-snug">
                          {trigger.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Footer — back to templates */}
              <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50">
                <button
                  onClick={() => setView('templates')}
                  className="text-[12px] text-gray-500 hover:text-gray-700 transition-colors"
                >
                  &larr; Back to templates
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
